import '@boltenergy-be/design-system/dist/assets/foundations.css'
import { useEffect } from 'react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { appWithTranslation, useTranslation } from 'next-i18next/pages'
import { Provider } from 'react-redux'
import { store } from 'store'
import nextI18NextConfig from '../../next-i18next.config.js'
import { startUp } from 'utils/startUp'
import 'slick-carousel/slick/slick.css'
import 'next-cloudinary/dist/cld-video-player.css'
import 'styles/globals.scss'
import StoreInitializer from 'store/StoreInitializer'
import 'dayjs/locale/fr'
import 'dayjs/locale/nl'
import dayjs from 'dayjs'
import ErrorBoundary from 'features/error-boundary/ErrorBoundary'
import { Language } from 'types/language'
import FeatureFlagOverlay from 'components/feature-flag-overlay/FeatureFlagOverlay'
import { IS_PROD } from 'constants/envs'

/**
 * next.config.js sets `skipTrailingSlashRedirect` so PostHog's proxied API requests (which rely on a
 * trailing slash) aren't redirected by Next's built-in trailing-slash handling. That setting also disables
 * Next's client-side trailing-slash normalization, which exposes a Next.js router quirk: it prefixes the
 * homepage ("/") with the locale via plain string concatenation, producing "/nl/" instead of "/nl". Next calls
 * history.replaceState with that value from deep inside its router, well after hydration and outside of any
 * public router event, so it can only be corrected by patching history.replaceState/pushState directly - a
 * useEffect reacting to route changes is too late to catch it.
 *
 * Also a fix for PostHog & Google Analytics double homepage page visit events.
 */
if (typeof window !== 'undefined') {
  const stripTrailingSlash = (url: string | URL | null | undefined): string | URL | null | undefined => {
    if (typeof url !== 'string') return url
    const { pathname, search, hash } = new URL(url, window.location.origin)
    return pathname !== '/' && pathname.endsWith('/') ? `${pathname.replace(/\/+$/, '')}${search}${hash}` : url
  }

  const originalReplaceState = window.history.replaceState.bind(window.history)
  const originalPushState = window.history.pushState.bind(window.history)

  window.history.replaceState = (data, unused, url) => originalReplaceState(data, unused, stripTrailingSlash(url))
  window.history.pushState = (data, unused, url) => originalPushState(data, unused, stripTrailingSlash(url))
}

const App = ({ Component, pageProps, router }: AppProps) => {
  // Initialize dayjs with the correct locale
  const { i18n } = useTranslation()
  dayjs.locale(i18n.resolvedLanguage)

  /**
   * Triggered on first render
   * Executes the startUp function
   */
  useEffect(() => {
    if (router.locale !== 'default') {
      startUp()
    }
  }, [])

  /**
   * Don't render pages for 'default' locale:
   * in Next.js, pages will be build for all locales, but because we're using the middleware "hack" to prefix routes with the locale,
   * we should prevent rendering by returning null (will speed up the build).
   * See https://nextjs.org/docs/advanced-features/i18n-routing#prefixing-the-default-locale
   */
  if (router.locale === 'default') {
    return null
  }

  return (
    <>
      <Provider store={store}>
        <StoreInitializer>
          {/* Default SEO */}
          <Head>
            {/* Favicon */}
            <link rel="icon" type="image/png" href="/favicon-96x96.png" sizes="96x96" />
            <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
            <link rel="shortcut icon" href="/favicon.ico" />
            <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
            <meta name="apple-mobile-web-app-title" content="Bolt" />
            <link rel="manifest" href="/site.webmanifest" />

            <meta name="viewport" content="initial-scale=1.0, width=device-width" />
            <meta
              property="og:image"
              content={`/images/seo/fallback-${router.locale === Language.FRENCH ? 'fr' : 'nl'}.webp`}
              key="image"
            />
            <meta name="twitter:site" content="@boltenergie" />
            <meta name="twitter:card" content="summary_large_image" />

            {/* Bing Webmaster tool for SEO performance validation */}
            <meta name="msvalidate.01" content="E379816734D575652C81F337A7AF62F3" />

            {/* Preload fonts used above the fold. Only woff2 (if available) as it's the most used. */}
            <link
              rel="preload"
              href="https://cdn.prod.backend.boltenergie.be/fonts/woff2/gamay-extrabold.woff2"
              as="font"
              type="font/woff2"
              fetchPriority="high"
              crossOrigin="anonymous"
            />
            <link
              rel="preload"
              href="https://cdn.prod.backend.boltenergie.be/fonts/woff2/gamay-book.woff2"
              as="font"
              type="font/woff2"
              fetchPriority="high"
              crossOrigin="anonymous"
            />
            <link
              rel="preload"
              href="https://cdn.prod.backend.boltenergie.be/fonts/woff2/gamay-medium.woff2"
              as="font"
              type="font/woff2"
              fetchPriority="high"
              crossOrigin="anonymous"
            />
            <link
              rel="preload"
              href="https://cdn.prod.backend.boltenergie.be/fonts/woff2/gelica-medium-italic.woff2"
              as="font"
              type="font/woff2"
              fetchPriority="high"
              crossOrigin="anonymous"
            />
          </Head>

          <ErrorBoundary>
            {/* Page Component */}
            <Component {...pageProps} />

            {/* Modal parent */}
            <div id="modal-root" />

            {/* DS Modal portal */}
            <div id="modal-portal" />

            {!IS_PROD && <FeatureFlagOverlay />}
          </ErrorBoundary>
        </StoreInitializer>
      </Provider>
    </>
  )
}

export default appWithTranslation(App, nextI18NextConfig)
